/**
 * useFreshness (T29, D10) — "am I the latest build, or a stale/cached tab?"
 *
 * The build stamp baked into the running bundle reports the RUNNING build,
 * which an open or bfcached tab keeps after a deploy (no service worker holds
 * it — a plain reload fixes it). The only honest "am I latest?" signal is to
 * fetch the freshly-deployed `/version.json` (served `no-store`, same origin)
 * and compare its `build` to the running `__APP_BUILD__.build`.
 *
 * Re-checks on `visibilitychange → visible` so a long-open tab notices a
 * deploy when the user returns to it. `catch → not-stale` — local dev has no
 * `version.json` (Vite dev serves nothing at that path), so a fetch failure
 * must NEVER raise a false alarm.
 */

import { useCallback, useEffect, useState } from 'react';

export interface Freshness {
  isStale: boolean;
  runningBuild: string;
  latestBuild: string | null;
}

async function fetchLatestBuild(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { build?: string };
    return typeof body.build === 'string' ? body.build : null;
  } catch {
    return null;
  }
}

export function useFreshness(): Freshness {
  const runningBuild = __APP_BUILD__.build;
  const [latestBuild, setLatestBuild] = useState<string | null>(null);

  const check = useCallback(async () => {
    const latest = await fetchLatestBuild();
    setLatestBuild(latest);
  }, []);

  useEffect(() => {
    void check();
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  // Stale only when we have a latest build AND it differs from what's running.
  // A null latest (offline / local / no version.json) is never stale.
  const isStale = latestBuild !== null && latestBuild !== runningBuild;

  return { isStale, runningBuild, latestBuild };
}
